/**
 * Avoid Formik swallowing errors thrown from onSubmit
 *
 * We recommend to wrap formik's onSubmit handlers into this helper.
 *
 * ## Problem
 * Formik swallows errors from onSubmit in some cases (feel free to add links
 * to other such cases if you discover them). I unfortunately don't remember other
 * cases, but I know we aren't facing such issue for the first time and it was not
 * only related to unmounted components.
 *
 * - unmounted component: https://github.com/formium/formik/issues/3390
 *
 * ## Solution
 * The solution is to handle errors inside submit handlers and never actually throw
 * over onSubmit boundary (which is a bad idea anyway unless you want the error to be caught
 * by global handler, because there is no way to try-catch around event prop - in this case &lt;form onSubmit&gt;.
 *
 * This helper function ensures you don't do that - and if you do, it re-throws the error asynchronously
 * to ensure it reaches global error handler to fire notifications or so, and writes brief explanation
 * to console.warn.
 *
 */
export const makeFormikOnSubmit =
  <TArgs extends unknown[], TReturn>(
    fn: (...args: TArgs) => TReturn | Promise<TReturn>,
  ): ((...args: TArgs) => Promise<TReturn>) =>
  async (...args) => {
    try {
      return await fn(...args);
    } catch (e) {
      // biome-ignore lint/suspicious/noConsole: intentional, do not show more errors in GUI as there is already rethrown e
      console.warn(
        'Formik onSubmit handler has thrown. This is discouraged, see makeFormikOnSubmit doc for details. Rethrowing asynchronously to ensure notification.',
        e,
      );
      setTimeout(() => {
        throw e;
      }, 0);
      throw e;
    }
  };
